import { useRef, useState } from "react";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faTriangleExclamation,
  faCircleQuestion,
  faWandMagicSparkles,
  faLayerGroup,
  faPlus,
  faXmark
} from "@fortawesome/free-solid-svg-icons";

import { hexToRgba } from "@aclymatepackages/converters";
import { productMaterials, productCategories } from "@aclymatepackages/lists";

import { COMPONENT_SECTIONS, summarizeExtraction } from "./extractionEngine";

const materialOptions = productMaterials.map(({ id, label }) => ({ id, label }));
const optionForId = (id) =>
  materialOptions.find((option) => option.id === id) || null;

const categoryOptions = productCategories.map(({ id, label }) => ({
  id,
  label
}));
const categoryOptionForId = (id) =>
  categoryOptions.find((option) => option.id === id) || null;
const subcategoryOptionsFor = (categoryId) => {
  const category = productCategories.find(({ id }) => id === categoryId);
  return (category?.subcategories || []).map(({ id, label }) => ({ id, label }));
};

const flagConfig = {
  reported: { colorKey: "secondary", icon: faCircleCheck, label: "Confirmed" },
  assumption: {
    colorKey: "warning",
    icon: faTriangleExclamation,
    label: "Assumption"
  },
  insufficient: {
    colorKey: "error",
    icon: faCircleQuestion,
    label: "Needs attention"
  }
};

const computeFlag = (material) => {
  if (!material.materialType) {
    return "insufficient";
  }
  if (
    (material.weight == null || material.weight === "") &&
    (material.percent == null || material.percent === "")
  ) {
    return "assumption";
  }
  return "reported";
};

const toNumberOrNull = (value) => {
  if (value === "" || value == null) {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const FlagChip = ({ flag }) => {
  const { palette } = useTheme();
  const config = flagConfig[flag];
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={
        <FontAwesomeIcon
          icon={config.icon}
          style={{ color: palette[config.colorKey].main }}
        />
      }
      label={config.label}
      sx={{
        color: palette[config.colorKey].main,
        borderColor: palette[config.colorKey].main
      }}
    />
  );
};

const MaterialRow = ({ material, onChange, onDelete }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      py: 0.75,
      flexWrap: "wrap"
    }}
  >
    <TextField
      size="small"
      label="Raw composition"
      value={material.rawName || ""}
      onChange={(event) => onChange({ ...material, rawName: event.target.value })}
      sx={{ flex: 2, minWidth: 200 }}
    />
    <Autocomplete
      size="small"
      options={materialOptions}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      value={optionForId(material.materialType)}
      onChange={(_, option) =>
        onChange({ ...material, materialType: option?.id || null })
      }
      renderInput={(params) => <TextField {...params} label="Material" />}
      sx={{ flex: 2, minWidth: 200 }}
    />
    <TextField
      size="small"
      label="Weight (g)"
      type="number"
      value={material.weight ?? ""}
      onChange={(event) =>
        onChange({ ...material, weight: toNumberOrNull(event.target.value) })
      }
      sx={{ width: 110 }}
    />
    <TextField
      size="small"
      label="% of comp."
      type="number"
      value={material.percent ?? ""}
      onChange={(event) =>
        onChange({ ...material, percent: toNumberOrNull(event.target.value) })
      }
      sx={{ width: 110 }}
    />
    <TextField
      select
      size="small"
      label="Value is"
      value={material.valueKind || "none"}
      onChange={(event) => onChange({ ...material, valueKind: event.target.value })}
      sx={{ width: 140 }}
    >
      <MenuItem value="weight">Weight</MenuItem>
      <MenuItem value="percentage">Percentage</MenuItem>
      <MenuItem value="none">Not provided</MenuItem>
    </TextField>
    <FlagChip flag={material.flag} />
    <Tooltip title="Remove this material">
      <IconButton size="small" onClick={onDelete}>
        <FontAwesomeIcon icon={faXmark} />
      </IconButton>
    </Tooltip>
  </Box>
);

const ComponentCard = ({ component, onChange, onDelete, makeUiId }) => {
  const { palette } = useTheme();

  const updateMaterial = (materialIndex, updatedMaterial) => {
    const next = {
      ...updatedMaterial,
      flag: computeFlag(updatedMaterial)
    };
    onChange({
      ...component,
      materials: component.materials.map((material, index) =>
        index === materialIndex ? next : material
      )
    });
  };

  const deleteMaterial = (materialIndex) =>
    onChange({
      ...component,
      materials: component.materials.filter(
        (_, index) => index !== materialIndex
      )
    });

  const addMaterial = () =>
    onChange({
      ...component,
      materials: [
        ...component.materials,
        {
          _uiId: makeUiId("m"),
          rawName: "",
          materialType: null,
          weight: null,
          percent: null,
          valueKind: "none",
          flag: "insufficient"
        }
      ]
    });

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 1.5, backgroundColor: palette.background.paper }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <TextField
          size="small"
          label="Component name"
          value={component.name || ""}
          onChange={(event) => onChange({ ...component, name: event.target.value })}
          sx={{ flex: 1 }}
        />
        <TextField
          select
          size="small"
          label="Section"
          value={component.section || ""}
          onChange={(event) =>
            onChange({ ...component, section: event.target.value || null })
          }
          helperText={
            component.section === "Packaging"
              ? "Goes to the Packaging tab"
              : undefined
          }
          sx={{ width: 170 }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {COMPONENT_SECTIONS.map((section) => (
            <MenuItem key={section} value={section}>
              {section}
            </MenuItem>
          ))}
          {component.section &&
            !COMPONENT_SECTIONS.includes(component.section) && (
              <MenuItem value={component.section}>{component.section}</MenuItem>
            )}
        </TextField>
        <Tooltip title="Remove this component">
          <IconButton size="small" onClick={onDelete}>
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </Tooltip>
      </Box>
      <Stack
        divider={
          <Box sx={{ borderBottom: `1px solid ${palette.backgroundGray.main}` }} />
        }
      >
        {component.materials.map((material, index) => (
          <MaterialRow
            key={material._uiId || `material-${index}`}
            material={material}
            onChange={(updated) => updateMaterial(index, updated)}
            onDelete={() => deleteMaterial(index)}
          />
        ))}
      </Stack>
      <Box sx={{ mt: 1 }}>
        <Button
          size="small"
          startIcon={<FontAwesomeIcon icon={faPlus} />}
          onClick={addMaterial}
        >
          Add material
        </Button>
      </Box>
    </Paper>
  );
};

const SummaryStat = ({ icon, colorKey, count, label }) => {
  const { palette } = useTheme();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <FontAwesomeIcon icon={icon} style={{ color: palette[colorKey].main }} />
      <Typography variant="subtitle2">
        {count} {label}
      </Typography>
    </Box>
  );
};

const BomConfirmationScreen = ({ extraction, onConfirm, onCancel }) => {
  const { palette } = useTheme();
  const uiIdCounterRef = useRef(0);
  const makeUiId = (prefix) => {
    uiIdCounterRef.current += 1;
    return `${prefix}-${uiIdCounterRef.current}`;
  };
  const [components, setComponents] = useState(() =>
    (extraction.components || []).map((component) => ({
      ...component,
      _uiId: makeUiId("c"),
      materials: (component.materials || []).map((material) => ({
        ...material,
        _uiId: makeUiId("m")
      }))
    }))
  );
  const [product, setProduct] = useState({
    name: extraction.product?.name || "",
    sku: extraction.product?.sku || "",
    category: extraction.product?.category || null,
    subcategory: extraction.product?.subcategory || null,
    countryOfOrigin: extraction.product?.countryOfOrigin || "",
    cityOfOrigin: extraction.product?.cityOfOrigin || "",
    portOfOrigin: extraction.product?.portOfOrigin || "",
    productWeightGrams: extraction.product?.productWeightGrams ?? null,
    packagingWeightGrams: extraction.product?.packagingWeightGrams ?? null,
    totalWeightGrams: extraction.product?.totalWeightGrams ?? null
  });
  const [templateName, setTemplateName] = useState("");

  const summary = summarizeExtraction({ ...extraction, components });

  const updateComponent = (componentIndex, updatedComponent) =>
    setComponents((current) =>
      current.map((component, index) =>
        index === componentIndex ? updatedComponent : component
      )
    );

  const deleteComponent = (componentIndex) =>
    setComponents((current) =>
      current.filter((_, index) => index !== componentIndex)
    );

  const addComponent = () =>
    setComponents((current) => [
      ...current,
      {
        _uiId: makeUiId("c"),
        name: "",
        section: null,
        materials: []
      }
    ]);

  const updateProductField = (field, value) =>
    setProduct((current) => ({ ...current, [field]: value }));

  const handleConfirm = () => {
    const materialTypeOverrides = Object.fromEntries(
      components.flatMap(({ materials }) =>
        materials
          .filter(
            ({ rawName, materialType }) =>
              rawName && rawName.trim() && materialType
          )
          .map(({ rawName, materialType }) => [
            rawName.trim().toLowerCase(),
            materialType
          ])
      )
    );

    const sanitizedComponents = components.map(
      ({ _uiId: _componentUiId, materials, ...component }) => ({
        ...component,
        materials: materials.map(
          ({ _uiId: _materialUiId, ...material }) => material
        )
      })
    );

    const confirmedExtraction = {
      ...extraction,
      product: {
        ...extraction.product,
        name: product.name || null,
        sku: product.sku || null,
        category: product.category || null,
        subcategory: product.subcategory || null,
        countryOfOrigin: product.countryOfOrigin || null,
        cityOfOrigin: product.cityOfOrigin || null,
        portOfOrigin: product.portOfOrigin || null,
        productWeightGrams: product.productWeightGrams,
        packagingWeightGrams: product.packagingWeightGrams,
        totalWeightGrams: product.totalWeightGrams
      },
      components: sanitizedComponents
    };

    // Declarative, user-confirmed rules the server-side template parser
    // reuses on every later file of this format: the detected column roles,
    // the header row, and the sections the user confirmed per component name
    // (used only where a later file gives no section header of its own).
    const componentSectionOverrides = Object.fromEntries(
      components
        .filter(({ name, section }) => name && name.trim() && section)
        .map(({ name, section }) => [name.trim().toLowerCase(), section])
    );

    return onConfirm({
      formatKey: extraction.formatKey,
      ...(extraction.columnRoles ? { columnRoles: extraction.columnRoles } : {}),
      headerRowIndex: extraction.headerRowIndex ?? null,
      componentSectionOverrides,
      name: templateName || extraction.fileName,
      relevantSheet: extraction.relevantSheet,
      skippedSheets: extraction.skippedSheets,
      layout: extraction.layout,
      components: sanitizedComponents,
      materialTypeOverrides,
      confirmedExtraction
    });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <FontAwesomeIcon
          icon={faWandMagicSparkles}
          style={{ color: palette.primary.main }}
        />
        <Typography variant="h6">
          Here&apos;s what we found — edit anything that&apos;s wrong, then confirm
        </Typography>
      </Box>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        We read <strong>{extraction.fileName}</strong> for you. Fix any
        mis-extracted components, materials, or weights. Once you confirm, we
        save this as a reusable format and parse the rest of your files
        automatically.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          p: 1.5,
          mb: 2,
          borderRadius: 1,
          backgroundColor: hexToRgba(palette.primary.main, 0.06)
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FontAwesomeIcon
            icon={faLayerGroup}
            style={{ color: palette.text.secondary }}
          />
          <Typography variant="body2">
            Read tab: <strong>{extraction.relevantSheet}</strong> ·{" "}
            {extraction.layout === "key-value"
              ? "vertical layout"
              : "table layout"}
          </Typography>
        </Box>
        {!!extraction.skippedSheets.length && (
          <Typography variant="caption" color="textSecondary">
            Skipped: {extraction.skippedSheets.join(", ")}
          </Typography>
        )}
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Product details
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <TextField
            size="small"
            label="Product name"
            value={product.name}
            onChange={(event) => updateProductField("name", event.target.value)}
            sx={{ flex: 2, minWidth: 220 }}
          />
          <TextField
            size="small"
            label="SKU / Model code"
            value={product.sku}
            onChange={(event) => updateProductField("sku", event.target.value)}
            sx={{ flex: 1, minWidth: 160 }}
          />
          <Autocomplete
            size="small"
            options={categoryOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, current) => option.id === current.id}
            value={categoryOptionForId(product.category)}
            onChange={(_, option) => {
              updateProductField("category", option?.id || null);
              updateProductField("subcategory", null);
            }}
            renderInput={(params) => <TextField {...params} label="Category" />}
            sx={{ flex: 1, minWidth: 180 }}
          />
          <Autocomplete
            size="small"
            options={subcategoryOptionsFor(product.category)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, current) => option.id === current.id}
            value={
              subcategoryOptionsFor(product.category).find(
                ({ id }) => id === product.subcategory
              ) || null
            }
            onChange={(_, option) =>
              updateProductField("subcategory", option?.id || null)
            }
            renderInput={(params) => (
              <TextField {...params} label="Subcategory" />
            )}
            sx={{ flex: 1, minWidth: 180 }}
            disabled={!product.category}
          />
          <TextField
            size="small"
            label="Country of origin"
            value={product.countryOfOrigin}
            onChange={(event) =>
              updateProductField("countryOfOrigin", event.target.value)
            }
            sx={{ flex: 1, minWidth: 160 }}
          />
          <TextField
            size="small"
            label="City of origin"
            value={product.cityOfOrigin}
            onChange={(event) =>
              updateProductField("cityOfOrigin", event.target.value)
            }
            sx={{ flex: 1, minWidth: 160 }}
          />
          <TextField
            size="small"
            label="Shipping port"
            value={product.portOfOrigin}
            onChange={(event) =>
              updateProductField("portOfOrigin", event.target.value)
            }
            helperText="Used for the default route; preferred over city"
            sx={{ flex: 1, minWidth: 160 }}
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          Weights — enter any 2 of 3 and the system computes the third (all in
          grams)
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <TextField
            size="small"
            label="Product weight (g)"
            type="number"
            value={product.productWeightGrams ?? ""}
            onChange={(event) =>
              updateProductField(
                "productWeightGrams",
                toNumberOrNull(event.target.value)
              )
            }
            sx={{ width: 180 }}
          />
          <TextField
            size="small"
            label="Packaging weight (g)"
            type="number"
            value={product.packagingWeightGrams ?? ""}
            onChange={(event) =>
              updateProductField(
                "packagingWeightGrams",
                toNumberOrNull(event.target.value)
              )
            }
            sx={{ width: 180 }}
          />
          <TextField
            size="small"
            label="Total weight (g)"
            type="number"
            value={product.totalWeightGrams ?? ""}
            onChange={(event) =>
              updateProductField(
                "totalWeightGrams",
                toNumberOrNull(event.target.value)
              )
            }
            sx={{ width: 180 }}
          />
        </Box>
      </Paper>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 2 }}>
        <Typography variant="subtitle1">
          {summary.componentCount} components · {summary.materialCount} materials
        </Typography>
        <SummaryStat
          icon={faCircleCheck}
          colorKey="secondary"
          count={summary.reportedCount}
          label="confirmed"
        />
        <SummaryStat
          icon={faTriangleExclamation}
          colorKey="warning"
          count={summary.assumptionCount}
          label="assumptions"
        />
        <SummaryStat
          icon={faCircleQuestion}
          colorKey="error"
          count={summary.insufficientCount}
          label="need attention"
        />
      </Box>

      {!components.length && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          We couldn&apos;t read components from this file. Add them manually
          below.
        </Alert>
      )}

      {components.map((component, index) => (
        <ComponentCard
          key={component._uiId || `component-${index}`}
          component={component}
          onChange={(updated) => updateComponent(index, updated)}
          onDelete={() => deleteComponent(index)}
          makeUiId={makeUiId}
        />
      ))}

      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<FontAwesomeIcon icon={faPlus} />}
          onClick={addComponent}
        >
          Add component
        </Button>
      </Box>

      <TextField
        fullWidth
        size="small"
        label="Name this format (e.g. customer name)"
        value={templateName}
        onChange={(event) => setTemplateName(event.target.value)}
        placeholder={extraction.fileName}
        sx={{ my: 2 }}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button color="inherit" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="secondary" onClick={handleConfirm}>
          Confirm &amp; save format
        </Button>
      </Box>
    </Box>
  );
};

export default BomConfirmationScreen;
